import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import api from "@/api";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, Mail, Lock, User, Eye, EyeOff, Loader2 } from "lucide-react";

const fmtErr = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : "")).filter(Boolean).join(" ") || "Please check your details.";
  if (detail && typeof detail.msg === "string") return detail.msg;
  return "Something went wrong. Please try again.";
};

const Input = ({ icon: Icon, testid, trailing, ...props }) => (
  <div className="relative">
    {Icon && <Icon size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />}
    <input
      data-testid={testid}
      {...props}
      className="w-full h-[52px] pl-11 pr-11 bg-surfaceSecondary border border-border rounded-[14px] text-base text-onSurface placeholder:text-muted-foreground focus:outline-none focus:border-brandPrimary transition-colors"
    />
    {trailing}
  </div>
);

export default function EmailAuth() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setAuth } = useAuth();
  const [mode, setMode] = useState(location.state?.mode || "signup"); // signup | login | verify | forgot | reset
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);

  const finishAuth = (data) => {
    localStorage.setItem("docket_token", data.token);
    setAuth(data.user, data.access);
    navigate(data.user.onboarded ? "/home" : "/onboarding", { replace: true });
  };

  const submit = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (mode === "signup") {
        await api.post("/auth/register", { email, password, name });
        toast.success("Code sent — check your email");
        setCode(""); setMode("verify");
      } else if (mode === "login") {
        const { data } = await api.post("/auth/login", { email, password });
        finishAuth(data);
      } else if (mode === "verify") {
        const { data } = await api.post("/auth/verify", { email, code });
        finishAuth(data);
      } else if (mode === "forgot") {
        await api.post("/auth/forgot-password", { email });
        toast.success("If that email exists, a reset code is on its way");
        setCode(""); setMode("reset");
      } else if (mode === "reset") {
        await api.post("/auth/reset-password", { email, code, password });
        toast.success("Password updated — please log in");
        setPassword(""); setMode("login");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 403 && detail === "unverified") {
        toast.message("Please verify your email — we've sent you a code");
        setCode(""); setMode("verify");
      } else {
        toast.error(fmtErr(detail) || "Something went wrong");
      }
    } finally { setBusy(false); }
  };

  const resend = async () => {
    try { await api.post("/auth/resend", { email }); toast.success("New code sent"); }
    catch { toast.error("Couldn't resend just yet — try in a moment"); }
  };

  const canSubmit = () => {
    if (mode === "signup") return email.includes("@") && password.length >= 8;
    if (mode === "login") return email.includes("@") && password.length >= 1;
    if (mode === "verify") return code.trim().length === 4;
    if (mode === "forgot") return email.includes("@");
    if (mode === "reset") return code.trim().length === 4 && password.length >= 8;
    return false;
  };

  const meta = {
    signup: { title: "Create your account", sub: "Start your 7-day free trial — no card needed.", cta: "Send verification code" },
    login: { title: "Welcome back", sub: "Log in to your Docket account.", cta: "Log in" },
    verify: { title: "Check your email", sub: `Enter the 4-digit code we sent to ${email || "your email"}.`, cta: "Verify & continue" },
    forgot: { title: "Reset your password", sub: "We'll email you a 4-digit code to reset it.", cta: "Send reset code" },
    reset: { title: "Set a new password", sub: `Enter the code sent to ${email || "your email"} and your new password.`, cta: "Update password" },
  }[mode];

  const pwToggle = (
    <button type="button" onClick={() => setShowPw((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" data-testid="toggle-password">
      {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
    </button>
  );

  return (
    <div className="min-h-screen bg-surface flex justify-center">
      <div className="w-full max-w-md px-6 pt-14 pb-10 flex flex-col">
        <button data-testid="email-auth-back" onClick={() => (mode === "signup" || mode === "login" ? navigate("/") : setMode(mode === "verify" ? "signup" : mode === "reset" ? "forgot" : "login"))} className="w-10 h-10 -ml-2 rounded-full flex items-center justify-center hover:bg-surfaceTertiary mb-6">
          <ArrowLeft size={20} className="text-onSurface" />
        </button>

        <h1 className="font-heading text-3xl font-extrabold text-onSurface leading-tight">{meta.title}</h1>
        <p className="text-sm text-muted-foreground mt-2 mb-8 leading-relaxed">{meta.sub}</p>

        <div className="flex flex-col gap-3.5">
          {mode === "signup" && <Input icon={User} testid="input-name" placeholder="Business name (optional)" value={name} onChange={(e) => setName(e.target.value)} />}

          {(mode === "signup" || mode === "login" || mode === "forgot") && (
            <Input icon={Mail} testid="input-email" type="email" inputMode="email" autoCapitalize="none" autoCorrect="off" placeholder="you@business.co.uk" value={email} onChange={(e) => setEmail(e.target.value)} />
          )}

          {(mode === "verify" || mode === "reset") && (
            <Input icon={Mail} testid="input-code" inputMode="numeric" maxLength={4} placeholder="4-digit code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 4))} />
          )}

          {(mode === "signup" || mode === "login" || mode === "reset") && (
            <Input icon={Lock} testid="input-password" type={showPw ? "text" : "password"} placeholder={mode === "reset" ? "New password (min 8 chars)" : "Password"} value={password} onChange={(e) => setPassword(e.target.value)} trailing={pwToggle} />
          )}
        </div>

        {mode === "login" && (
          <button data-testid="link-forgot" onClick={() => setMode("forgot")} className="text-sm font-semibold text-brandPrimary self-start mt-3">Forgot password?</button>
        )}

        <button
          data-testid="email-auth-submit"
          onClick={submit}
          disabled={!canSubmit() || busy}
          className="w-full mt-7 bg-brandPrimary text-onBrand rounded-full h-14 font-heading font-extrabold text-base flex items-center justify-center gap-2 hover:bg-brandSecondary transition-colors active:scale-[0.98] disabled:opacity-40 disabled:active:scale-100"
        >
          {busy ? <Loader2 size={20} className="animate-spin" /> : meta.cta}
        </button>

        {(mode === "verify") && (
          <button data-testid="link-resend" onClick={resend} className="text-sm font-semibold text-muted-foreground mt-4 self-center">Didn't get it? <span className="text-brandPrimary">Resend code</span></button>
        )}

        <div className="mt-auto pt-8 text-center">
          {mode === "signup" && <p className="text-sm text-muted-foreground">Already have an account? <button data-testid="switch-login" onClick={() => setMode("login")} className="font-bold text-brandPrimary">Log in</button></p>}
          {mode === "login" && <p className="text-sm text-muted-foreground">New to Docket? <button data-testid="switch-signup" onClick={() => setMode("signup")} className="font-bold text-brandPrimary">Create an account</button></p>}
        </div>
      </div>
    </div>
  );
}
